<template>

      <div v-if="order" class="mb-4">

        <!-- Hlavička objednávky -->
        <div class="d-flex align-items-center gap-3 mb-4 flex-wrap">
          <h5 class="mb-0" v-if="isSupplement">Dodatečná objednávka č. {{ order.counterNumber }} k objednávce č. {{ order.custom?.parentOrderNumber }}</h5>
          <h5 class="mb-0" v-else>Objednávka č. {{ order.counterNumber }}</h5>
          <span :class="['badge', 'rounded-pill', 'px-3', 'py-1', statusClass(order.status)]">
            {{ statusLabel(order.status) }}
          </span>
          <span class="text-muted small ms-auto">Vytvořeno: {{ formatDateTime(order.createdAt) }}</span>
        </div>

        <!-- Adresa + poznámka -->
        <div class="row g-3 mb-4">
          <div class="col-md-6">
            <div class="bg-light p-3 rounded h-100">
              <h6 class="fw-semibold mb-2"><i class="bi bi-geo-alt-fill me-1"></i>Adresa doručení</h6>
              <address v-if="shippingAddress" class="mb-0">
                <strong>{{ shippingAddress.company }}</strong><br>
                <template v-if="shippingAddress.custom?.nazevd">
                  {{ shippingAddress.custom.nazevd }}
                </template>
                <template v-else>
                  {{ shippingAddress.street }}<br>
                  {{ shippingAddress.zip }} {{ shippingAddress.city }}
                </template>
              </address>
              <div v-else class="alert alert-warning mb-0 py-2 px-3 small">
                U této objednávky chybí doručovací adresa. Pokud problém přetrvá, kontaktujte
                <a href="mailto:webmaster@girafruit.cz">webmaster@girafruit.cz</a>.
              </div>
            </div>
          </div>
          <div class="col-md-6" v-if="order.custom?.note || order.custom?.deliveryDate">
            <div class="bg-light p-3 rounded h-100">
              <h6 class="fw-semibold mb-2"><i class="bi bi-info-circle me-1"></i>Informace</h6>
              <p v-if="order.custom.deliveryDate" class="mb-1">
                <i class="bi bi-calendar-event me-1 text-secondary"></i>
                <strong>Doručit dne:</strong> {{ formatDate(order.custom.deliveryDate) }}
              </p>
              <p v-if="order.custom.note" class="mb-0">
                <i class="bi bi-chat-left-text me-1 text-secondary"></i>
                <strong>Poznámka:</strong> {{ order.custom.note }}
              </p>
            </div>
          </div>
        </div>

        <!-- Objednané položky -->
        <h6 class="fw-semibold mb-2">Objednané položky</h6>
        <table class="table table-sm mb-4">
          <thead>
          <tr>
            <th>Kód</th>
            <th>Název</th>
            <th>Množství</th>
            <th>Jednotka</th>
            <th v-if="hasPrices" class="text-end">Cena/j bez DPH</th>
            <th v-if="hasPrices" class="text-end">Celkem bez DPH</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in orderedItems" :key="'ordered-' + index">
            <td>{{ item.code }}</td>
            <td>{{ item.name }}</td>
            <td>{{ item.quantity }}</td>
            <td>{{ item.custom?.orderUnit ?? item.unit }}</td>
            <td v-if="hasPrices" class="text-end text-nowrap">{{ priceOrDash(item.custom?.customerPrice) }}</td>
            <td v-if="hasPrices" class="text-end text-nowrap">{{ priceOrDash(item.custom?.customerLineTotal) }}</td>
          </tr>
          </tbody>
          <tfoot v-if="hasPrices">
          <tr>
            <th colspan="5" class="text-end">Orientační cena bez DPH</th>
            <th class="text-end text-nowrap order-total">{{ priceOrDash(order.custom?.customerTotal) }}</th>
          </tr>
          </tfoot>
        </table>
        <p v-if="hasPrices" class="small text-muted mt-n3 mb-4">
          Ceny podle ceníku v okamžiku objednání. Cena je orientační, skutečná se řídí dovezeným zbožím.
        </p>

        <!-- Reklamované položky -->
        <div v-if="reclaimedItems.length">
          <h6 class="fw-semibold mb-2">Reklamované položky</h6>
          <table class="table table-sm">
            <thead>
            <tr>
              <th>Kód</th>
              <th>Název</th>
              <th>Množství</th>
              <th>Jednotka</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, index) in reclaimedItems" :key="'reclaimed-' + index">
              <td>{{ item.code }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ item.unit }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-else class="text-center text-muted">
        Načítání objednávky...
      </div>
</template>
<script lang="ts">
import {useAppStore} from "@/stores/appStore";
import {ResponseEntityOrder} from "@haxo-cz/orders-ts-client";
import {formatCzk} from "@/components/gira/orderRows";

export default {
  name: "OrderDetail",
  props:{
    orderId: {
      required: true,
      type: String
    }
  },
  data(){
    return {
      appStore: useAppStore(),
      order: null as ResponseEntityOrder | null,
    }
  },
  methods:{
    formatCzk,
    priceOrDash(value: unknown): string {
      return typeof value === 'number' ? formatCzk(value) : '–';
    },
    statusLabel(status: string): string {
      switch (status) {
        case 'created':   return 'Čeká na přijetí';
        case 'completed': return 'Přijato do systému';
        case 'error':     return 'Chyba při předání';
        case 'cancelled': return 'Zrušeno';
        default:          return status;
      }
    },
    statusClass(status: string): string {
      switch (status) {
        case 'created':   return 'bg-warning text-dark';
        case 'completed': return 'bg-success text-white';
        case 'error':     return 'bg-danger text-white';
        case 'cancelled': return 'bg-secondary text-white';
        default:          return 'bg-light text-dark';
      }
    },
    formatDateTime(dateStr: string): string {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleString('cs-CZ', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    findOrder(){
      if(this.appStore.selectedProject)
        this.$ordersApi.findOrder(
            this.appStore.selectedProject.spaceId,
            this.appStore.selectedProject._id,
            this.$route.params.orderId as string
        ).then((response) => {
          this.order = response.data;
        }).catch(e => {
              console.error("Chyba při načítání objednávky:", e);
            }
        )
    },
    formatDate(dateStr: string | null): string {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  },
  mounted(){
    this.findOrder();
  },
  computed:{
    shippingAddress(): any | null {
      const shipping = this.order?.addresses?.shipping as any;
      if (!shipping || typeof shipping === "string") {
        return null;
      }

      return shipping;
    },
    // Ceny jen u objednávek uložených se snímkem cen, starší se z dnešního ceníku nedopočítávají.
    hasPrices(): boolean {
      return typeof this.order?.custom?.customerTotal === 'number';
    },
    isSupplement(): boolean {
      return this.order?.custom?.orderType === 'supplement';
    },
    orderedItems(): any[] {
      return this.order?.items?.filter(item => !item.custom?.reclamation) || [];
    },
    reclaimedItems(): any[] {
      return this.order?.items?.filter(item => item.custom?.reclamation) || [];
    }
  }
}
</script>
